import { type EntitlementAccessConfig } from '@webapp/core/services/Entitlement/types'
import { Permissions } from '@webapp/platform/iam'
import { type ExportDataTypeConfig } from './ExportDataTypeConfig'

// Global-store snapshot supplied by callers (the list-exportable-data-types
// Model and the RequestedExportsPage route guard) — entities stay pure, no
// store imports here (BHV-002/003/004, BHV-036).
export interface ExportDataTypeVisibilityGates {
  isSuperuser: boolean
  granularPermissions: boolean // granular_permissions feature switch
  permissions: string[] // flat permission strings
  entitlements: EntitlementAccessConfig
  featureFlags: Record<string, boolean | undefined>
  featureSwitches: Record<string, boolean | undefined>
}

const isEntitlementEnabled = (
  entitlements: EntitlementAccessConfig,
  entitlementKey: string
): boolean => {
  const node = entitlementKey
    .split('.')
    .reduce<unknown>(
      (current, segment) =>
        current && typeof current === 'object'
          ? (current as Record<string, unknown>)[segment]
          : undefined,
      entitlements.module
    )

  return (
    !!node &&
    typeof node === 'object' &&
    (node as { enabled?: boolean }).enabled === true
  )
}

// Pure landing-row / route-guard visibility predicate. Gate rules:
// superuser-only rows require the superuser flag (BHV-004); entitlement,
// feature-flag and feature-switch gates must pass (BHV-002/004); permission
// rows require the per-type permission in granular mode (BHV-003) and the
// umbrella export_data permission otherwise (BHV-002).
export const isExportDataTypeVisible = (
  config: ExportDataTypeConfig,
  gates: ExportDataTypeVisibilityGates
): boolean => {
  const { visibility } = config

  if (visibility.superuserOnly && !gates.isSuperuser) {
    return false
  }

  if (
    visibility.requiredFeatureFlag &&
    gates.featureFlags[visibility.requiredFeatureFlag] !== true
  ) {
    return false
  }

  if (
    visibility.requiredFeatureSwitchesAny &&
    !visibility.requiredFeatureSwitchesAny.some(
      (featureSwitch) => gates.featureSwitches[featureSwitch] === true
    )
  ) {
    return false
  }

  if (
    visibility.requiredFeatureSwitchesOff &&
    visibility.requiredFeatureSwitchesOff.some(
      (featureSwitch) => gates.featureSwitches[featureSwitch] === true
    )
  ) {
    return false
  }

  if (
    visibility.entitlementKey &&
    !isEntitlementEnabled(gates.entitlements, visibility.entitlementKey)
  ) {
    return false
  }

  if (config.permission) {
    return gates.granularPermissions
      ? gates.permissions.includes(config.permission)
      : gates.permissions.includes(Permissions.SETTINGS_EXPORT_DATA)
  }

  return true
}
